import React, { useEffect, useState, useMemo } from 'react'
import { Button } from 'core/components/button'
import { Select } from 'core/components/form/select'
import { GetVehicleTypes } from 'components/vehicle'
import { classNames } from 'core/helpers/misc'
import { ReadOnlyInput } from 'core/components/form/readonly-input'
import { PlateMask, requiredMessage } from 'core/constants'
import { useForm } from 'core/hooks/form'
import { applyMask, maskMoney } from 'core/helpers/mask'
import { API } from 'service'
import { Field } from 'core/components/form/field'

const getDefaultTime = fees => {
  const fee = fees.find(f => f.minutes === 60)

  if (fee) return fee
  return fees[0]
}

const setDefaultTime = (form, operator, fees) => {
  const { vehicleTypeRequiredParking, blockRequiredParking } = operator.parameters

  if (!vehicleTypeRequiredParking && !blockRequiredParking) {
    const fee = getDefaultTime(fees)
    form.setValues({
      minutes: fee.minutes,
      value: fee.value,
    })
  }
}

const getFees = (operator, vehicleTypeId, regionId) => {
  const fees = operator.parameters.parkingFees
  let items = []

  if (vehicleTypeId) {
    items = fees.filter(i => i.vehicleTypeId === vehicleTypeId)
  }

  if (regionId) {
    items = fees.filter(i => i.regionId === regionId)
  }

  if (items.length === 0) {
    const fee = getDefaultTime(fees)
    items.push({
      id: fee.id,
      minutes: fee.minutes,
      value: fee.value,
    })
  }

  return items
}

function renderVehicleType(form, operator, setFees, vehicleTypesCity) {
  const { setValues } = form

  const changeCategory = vehicleType => {
    const fees = getFees(operator, vehicleType.id)
    const fee = getDefaultTime(fees)

    setValues({
      vehicleTypeId: vehicleType.id,
      vehicleTypeName: vehicleType.name,
      minutes: fee.minutes,
      value: fee.value,
      view: 'Plate',
    })

    setFees(fees)
  }

  return (
    <div className="vehicle-type">
      {vehicleTypesCity.map(ent => (
        <div
          className="item"
          key={ent.id}
          onClick={() => changeCategory(vehicleTypesCity.find(t => t.id === ent.id))}
        >
          <i className={ent.icon} />
          <span>{ent.name}</span>
        </div>
      ))}
    </div>
  )
}

function renderPlate(form, vehicles, operator) {
  const { entity, errors, touched, setValues, handleChange } = form

  const {
    regionRequiredParking,
    streetRequiredParking,
    blockRequiredParking,
    parkingLotRequiredParking,
  } = operator.parameters

  const submitPlate = ev => {
    ev.preventDefault()

    const callback = () => {
      if (touched.plate && !errors.plate) {
        setValues({
          view:
            regionRequiredParking ||
            streetRequiredParking ||
            blockRequiredParking ||
            parkingLotRequiredParking
              ? 'Local'
              : 'Time',
        })
      }
    }

    if (!touched.plate) {
      handleChange({ path: 'plate', values: {} })
    } else {
      callback()
    }
  }

  return (
    <form onSubmit={ev => submitPlate(ev)}>
      <Field className="field-xl" label="Placa">
        <Select
          getDisplay={v => v.plate}
          getId={v => v.plate}
          items={vehicles}
          meta={{ error: errors.plate, touched: touched.plate }}
          onChange={v => handleChange({ path: 'plate', values: { plate: v.plate } })}
          placeholder="Selecione o veículo..."
          selected={entity.plate}
        />
      </Field>

      <div className="col-lg kt-align-right">
        <Button customClassName="btn-secondary" icon="fas fa-arrow-right" title="Próximo" />
      </div>
    </form>
  )
}

function renderLocal(form, operator, setFees) {
  const { entity, touched, errors, setValues, handleChange } = form

  const {
    regionRequiredParking,
    streetRequiredParking,
    blockRequiredParking,
    parkingLotRequiredParking,
    regions,
    streets,
    streetBlocks,
    parkingLots,
  } = operator.parameters

  const changeRegion = region => {
    const fees = getFees(operator, entity.vehicleTypeId, region.id)
    if (fees.length > 0) {
      const fee = getDefaultTime(fees)

      handleChange({
        path: 'regionId',
        values: {
          regionId: region.id,
          regionName: region.name,
          minutes: fee.minutes,
          value: fee.value,
        },
      })
    } else {
      handleChange({
        path: 'regionId',
        values: {
          regionId: region.id,
          regionName: region.name,
        },
      })
    }

    setFees(fees)
  }

  const changeBlock = streetBlock => {
    const fees = getFees(operator, entity.vehicleTypeId, streetBlock.regionId)
    const fee = getDefaultTime(fees)

    handleChange({
      path: 'streetBlockId',
      values: {
        streetBlockId: streetBlock.id,
        streetBlockName: streetBlock.name,
        minutes: fee.minutes,
        value: fee.value,
      },
    })

    setFees(fees)
  }

  const submitLocal = ev => {
    ev.preventDefault()

    const callback = () => {
      if (!errors.regionId && !errors.streetId && !errors.streetBlockId && !errors.parkingLotId) {
        setValues({ view: 'Time' })
      }
    }

    if (!touched.regionId && regionRequiredParking) {
      handleChange({ path: 'regionId', values: {} })
    }
    if (!touched.streetId && streetRequiredParking) {
      handleChange({ path: 'streetId', values: {} })
    }
    if (!touched.streetBlockId && blockRequiredParking) {
      handleChange({ path: 'streetBlockId', values: {} })
    }
    if (!touched.parkingLotId && parkingLotRequiredParking) {
      handleChange({ path: 'parkingLotId', values: {} })
    }
    callback()
  }

  return (
    <form onSubmit={ev => submitLocal(ev)}>
      {regionRequiredParking && (
        <Field className="field-xl" label="Região">
          <Select
            getDisplay={r => r.name}
            getId={r => r.id}
            items={regions}
            meta={{
              error: errors.regionId,
              touched: touched.regionId,
            }}
            onChange={r => changeRegion(r)}
            placeholder="Selecione uma região..."
            selected={entity.regionId}
          />
        </Field>
      )}

      {streetRequiredParking && (
        <Field className="field-xl" label="Rua">
          <Select
            getDisplay={s => s.name}
            getId={s => s.id}
            items={streets}
            meta={{
              error: errors.streetId,
              touched: touched.streetId,
            }}
            onChange={s =>
              handleChange({
                path: 'streetId',
                values: {
                  streetId: s ? s.id : 0,
                  streetName: s.name,
                },
              })
            }
            placeholder="Selecione uma rua..."
            selected={entity.streetId}
          />
        </Field>
      )}

      {blockRequiredParking && (
        <Field className="field-xl" label="Quadra">
          <Select
            getDisplay={b => b.name}
            getId={b => b.id}
            items={streetBlocks}
            meta={{
              error: errors.streetBlockId,
              touched: touched.streetBlockId,
            }}
            onChange={b => changeBlock(b)}
            placeholder="Selecione uma quadra..."
            selected={entity.streetBlockId}
          />
        </Field>
      )}

      {parkingLotRequiredParking && (
        <Field className="field-xl" label="Vaga">
          <Select
            getDisplay={l => l.name}
            getId={l => l.id}
            items={parkingLots}
            meta={{
              error: errors.parkingLotId,
              touched: touched.parkingLotId,
            }}
            onChange={l =>
              handleChange({
                path: 'parkingLotId',
                values: {
                  parkingLotId: l ? l.id : 0,
                  parkingLotName: l.name,
                },
              })
            }
            placeholder="Selecione uma vaga..."
            selected={entity.parkingLotId}
          />
        </Field>
      )}

      <div className="col-lg kt-align-right">
        <Button customClassName="btn-secondary" icon="fas fa-arrow-right" title="Próximo" />
      </div>
    </form>
  )
}

function renderTime(form, operator, fees) {
  const { entity, setValues } = form

  const { useParkingFeeAccumulation } = operator.parameters

  return (
    <>
      {!useParkingFeeAccumulation && (
        <Field label="Tarifa">
          <ReadOnlyInput customClassName="form-control-xl" value={maskMoney(entity.value)} />
        </Field>
      )}

      <div className="col-lg kt-align-right">
        <Button
          customClassName="btn-secondary"
          icon="fas fa-arrow-right"
          onClick={() => setValues({ view: 'Confirm' })}
          title="Próximo"
        />
      </div>
    </>
  )
}

function renderConfirm(form, operator, modal, route, basename) {
  const { entity, submitting, setSubmitting } = form

  const {
    vehicleTypeRequiredParking,
    regionRequiredParking,
    streetRequiredParking,
    blockRequiredParking,
    parkingLotRequiredParking,
    useParkingFeeAccumulation,
  } = operator.parameters

  return (
    <form
      onSubmit={ev => {
        setSubmitting(true)
        ev.preventDefault()
        API.post('parking.credit', entity)
          .then(resp => {
            modal.alert(<pre>{resp.coupon}</pre>, () => route.history.push(`${basename}`))
          })
          .catch(err => modal.alert(err.message))
          .finally(() => setSubmitting(false))
      }}
    >
      <div>
        <div className="row">
          {vehicleTypeRequiredParking && (
            <div className="col-lg">
              <Field label="Categoria">
                <ReadOnlyInput customClassName="form-control-xl" value={entity.vehicleTypeName} />
              </Field>
            </div>
          )}

          <div className="col-lg">
            <Field label="Placa">
              <ReadOnlyInput customClassName="form-control-xl" value={entity.plate} />
            </Field>
          </div>
        </div>
        {(regionRequiredParking ||
          streetRequiredParking ||
          blockRequiredParking ||
          parkingLotRequiredParking) && (
          <div className="row">
            {regionRequiredParking && (
              <div className="col-lg">
                <Field label="Região">
                  <ReadOnlyInput customClassName="form-control-xl" value={entity.regionName} />
                </Field>
              </div>
            )}

            {streetRequiredParking && (
              <div className="col-lg">
                <Field label="Rua">
                  <ReadOnlyInput customClassName="form-control-xl" value={entity.streetName} />
                </Field>
              </div>
            )}

            {blockRequiredParking && (
              <div className="col-lg">
                <Field label="Quadra">
                  <ReadOnlyInput customClassName="form-control-xl" value={entity.streetBlockName} />
                </Field>
              </div>
            )}

            {parkingLotRequiredParking && (
              <div className="col-lg">
                <Field label="Vaga">
                  <ReadOnlyInput customClassName="form-control-xl" value={entity.parkingLotName} />
                </Field>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="row">
        {!useParkingFeeAccumulation && (
          <div className="col-lg">
            <Field label="Tarifa">
              <ReadOnlyInput customClassName="form-control-xl" value={maskMoney(entity.value)} />
            </Field>
          </div>
        )}
      </div>

      <div className="col-lg kt-align-right">
        <Button
          customClassName="btn-secondary btn-lg kt-font-xl kt-font-boldest"
          disabled={submitting}
          icon="fas fa-dollar-sign"
          loading={submitting}
          title="Estacionar"
        />
      </div>
    </form>
  )
}

export const ParkingForm = ({ global: { operator, modal }, route, basename }) => {
  const { state } = route.location
  const {
    vehicleTypeRequiredParking,
    regionRequiredParking,
    streetRequiredParking,
    blockRequiredParking,
    parkingLotRequiredParking,
  } = operator.parameters

  const vehicleTypes = useMemo(() => GetVehicleTypes(operator.parameters.vehicleTypes), [
    operator.parameters.vehicleTypes,
  ])

  const view = state
    ? regionRequiredParking ||
      streetRequiredParking ||
      blockRequiredParking ||
      parkingLotRequiredParking
      ? 'Local'
      : 'Confirm'
    : operator.parameters.vehicleTypeRequiredParking
    ? 'VehicleType'
    : 'Plate'

  const form = useForm(
    {
      displayName: ent => ent.name,
      initialEntity: {
        id: 0,
        plate: state ? applyMask(PlateMask, state.plate) : '',
        vehicleTypeId: state?.vehicleTypeId || 0,
        minutes: 0,
        value: 0,
        regionId: 0,
        streetId: 0,
        streetBlockId: 0,
        parkingLotId: 0,
        view,
      },
      validate: values => {
        const errors = {}
        const plate = values.plate.split(/_/).join('')

        if (!plate) {
          errors.plate = requiredMessage
        } else if (plate.length !== 8) {
          errors.plate = 'Digite o número de placa em um formato válido.'
        }

        if (!values.regionId && regionRequiredParking) {
          errors.regionId = requiredMessage
        }

        if (!values.streetId && streetRequiredParking) {
          errors.streetId = requiredMessage
        }

        if (!values.streetBlockId && blockRequiredParking) {
          errors.streetBlockId = requiredMessage
        }

        if (!values.parkingLotId && parkingLotRequiredParking) {
          errors.parkingLotId = requiredMessage
        }

        return errors
      },
    },
    route,
  )

  const [vehicles, setVehicles] = useState([])
  const [fees, setFees] = useState(operator.parameters.parkingFees)
  const { entity, errors, touched } = form

  useEffect(() => {
    setDefaultTime(form, operator, fees)
    API.post('vehicle.listOwner', { plates: [] }).then(({ items }) =>
      setVehicles(items.map(item => ({ ...item, plate: applyMask(PlateMask, item.plate) }))),
    )
    // eslint-disable-next-line
  }, [])

  return (
    <div className="kt-portlet">
      <div className="kt-portlet__head">
        <div className="kt-portlet__head-label">
          <span className="kt-portlet__head-icon">
            <i className="kt-font-brand fas fa-clock" />
          </span>
          <h3 className="kt-portlet__head-title">Ativar Cartão Digital</h3>
        </div>
      </div>
      <div className="kt-portlet__body">
        <ul className="nav nav-tabs kt-mb-0" role="tablist">
          {vehicleTypeRequiredParking && (
            <li className="nav-item">
              <button
                className={classNames('nav-link', {
                  active: entity.view === 'VehicleType',
                })}
                onClick={() =>
                  form.setValues({
                    view: 'VehicleType',
                  })
                }
                type="button"
              >
                <span className="btn kt-padding-0">Categoria</span>
              </button>
            </li>
          )}

          <li className="nav-item">
            <button
              className={classNames('nav-link', {
                active: entity.view === 'Plate',
                disabled: vehicleTypeRequiredParking && entity.vehicleTypeId === 0,
              })}
              onClick={() => form.setValues({ view: 'Plate' })}
              type="button"
            >
              <span className="btn kt-padding-0">Placa</span>
            </button>
          </li>

          {(regionRequiredParking ||
            streetRequiredParking ||
            blockRequiredParking ||
            parkingLotRequiredParking) && (
            <li className="nav-item">
              <button
                className={classNames('nav-link', {
                  active: entity.view === 'Local',
                  disabled: !touched.plate || !!errors.plate,
                })}
                onClick={() =>
                  form.setValues({
                    view: 'Local',
                  })
                }
                type="button"
              >
                <span className="btn kt-padding-0">Localização</span>
              </button>
            </li>
          )}

          <li className="nav-item">
            <button
              className={classNames('nav-item nav-link', {
                active: entity.view === 'Time',
                disabled:
                  (regionRequiredParking && entity.regionId === 0) ||
                  (streetRequiredParking && entity.streetId === 0) ||
                  (blockRequiredParking && entity.streetBlockId === 0) ||
                  (parkingLotRequiredParking && entity.parkingLotId === 0) ||
                  !touched.plate ||
                  errors.plate,
              })}
              onClick={() =>
                form.setValues({
                  view: 'Time',
                })
              }
              type="button"
            >
              <span className="btn kt-padding-0">Tarifa</span>
            </button>
          </li>

          <li className="nav-item">
            <button
              className={classNames('nav-item nav-link', {
                active: entity.view === 'Confirm',
                disabled:
                  (regionRequiredParking && entity.regionId === 0) ||
                  (streetRequiredParking && entity.streetId === 0) ||
                  (blockRequiredParking && entity.streetBlockId === 0) ||
                  (parkingLotRequiredParking && entity.parkingLotId === 0) ||
                  !touched.plate ||
                  errors.plate ||
                  entity.minutes === 0,
              })}
              onClick={() =>
                form.setValues({
                  view: 'Confirm',
                })
              }
              type="button"
            >
              <span className="btn kt-padding-0">Confirme os dados</span>
            </button>
          </li>
        </ul>

        <div className="border border-top-0 rounded-bottom p-3">
          {entity.view === 'VehicleType' &&
            renderVehicleType(form, operator, setFees, vehicleTypes)}
          {entity.view === 'Plate' && renderPlate(form, vehicles, operator)}
          {entity.view === 'Local' && renderLocal(form, operator, setFees)}
          {entity.view === 'Time' && renderTime(form, operator, fees)}
          {entity.view === 'Confirm' && renderConfirm(form, operator, modal, route, basename)}
        </div>
      </div>
    </div>
  )
}
